import React from 'react';
import { useSchedule } from '../context/ScheduleContext';
import { getCurrentAndNextPerformances } from '../utils/timeUtils';
import { parseISO, format, differenceInMinutes } from 'date-fns';
import { Clock, Heart, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const Marquee: React.FC = () => {
  const { t } = useTranslation();
  const { allPerformances, virtualTime, favorites, toggleFavorite } = useSchedule();
  
  const liveData = React.useMemo(() => {
    return getCurrentAndNextPerformances(allPerformances, virtualTime);
  }, [allPerformances, virtualTime]);

  if (liveData.length === 0) {
    return <div className="p-4 text-center">{t('common.noPerformancesFound')}</div>;
  }

  return (
    <div className="flex flex-col gap-4 p-4 pb-20">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Clock className="w-5 h-5 text-secondary" />
          {t('marquee.liveNow')}
        </h2>
        <div className="text-sm text-gray-400">
          {format(virtualTime, 'EEE HH:mm')}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {liveData.map(({ stage, current, next }) => (
          <div key={stage} className="bg-surface rounded-lg p-4 border border-gray-800 shadow-lg relative overflow-hidden group">
             {/* Stage Name Background Effect or Label */}
             <div className="absolute top-0 right-0 p-2 opacity-10 text-4xl font-black truncate max-w-full pointer-events-none">
                {stage}
             </div>
             
             <div className="relative z-10">
                <div className="flex justify-between items-center mb-2">
                  <Link to={`/schedule?stage=${encodeURIComponent(stage)}`} className="text-sm font-bold text-secondary uppercase tracking-wider hover:underline">
                    {stage}
                  </Link>
                  <Link
                    to={`/map?stage=${encodeURIComponent(stage)}`}
                    className="p-1 text-gray-400 hover:text-primary"
                    title={t('marquee.showOnMap')}
                  >
                    <MapPin className="w-4 h-4" />
                  </Link>
                </div>

                {/* Current Performance */}
                <div className="mb-4">
                  {current ? (
                    <div className="flex justify-between items-start">
                        <div>
                            <div className="text-xs text-gray-400 mb-1">{t('marquee.playingNow')}</div>
                            <h3 className="text-lg font-bold text-white leading-tight">{current.dj}</h3>
                            <div className="text-xs text-gray-500 mt-1">
                                {format(parseISO(current.start_time), 'HH:mm')} - {format(parseISO(current.end_time), 'HH:mm')}
                            </div>
                        </div>
                        <button 
                            onClick={(e) => { e.preventDefault(); toggleFavorite(current.dj); }}
                            className="p-2 hover:bg-white/10 rounded-full transition-colors"
                        >
                            <Heart 
                                className={`w-5 h-5 ${favorites.includes(current.dj) ? 'fill-alert text-alert' : 'text-gray-400'}`} 
                            />
                        </button>
                    </div>
                  ) : (
                    <div className="text-gray-500 italic">{t('marquee.nothingPlayingNow')}</div>
                  )}
                </div>

                {/* Next Performance */}
                {next && (
                  <div className="pt-3 border-t border-gray-700">
                     <div className="flex justify-between items-center">
                        <div>
                            <div className="text-xs text-gray-400">
                                {(() => {
                                  const mins = differenceInMinutes(parseISO(next.start_time), virtualTime);
                                  if (mins > 59) {
                                    const hrs = Math.floor(mins / 60);
                                    const remainingMins = mins % 60;
                                    const minutesPart =
                                      remainingMins > 0
                                        ? ` ${t('time.minutesShort', { minutes: remainingMins })}`
                                        : '';
                                    return t('marquee.upNextHoursMins', {
                                      hours: hrs,
                                      minutesPart,
                                    });
                                  }
                                  return t('marquee.upNextMins', { mins });
                                })()}
                            </div>
                            <div className="font-semibold text-gray-300">{next.dj}</div>
                        </div>
                        <div className="text-xs text-gray-500">
                            {format(parseISO(next.start_time), 'HH:mm')}
                        </div>
                     </div>
                  </div>
                )}
             </div>
          </div>
        ))}
      </div>
    </div>
  );
};
